import type { ScoreCell, ScoreRow } from './results';
import { digits, fold, type SortDirection } from './sorting';

/**
 * Finding and ordering the class's marks.
 *
 * In the browser, unlike the sittings beside it: the score table is one row
 * per candidate and arrives whole, so there is no page to search a fraction
 * of. It is also the table a teacher reads down while reading names out, which
 * is an argument for it answering instantly.
 */
export type ScoreSortKey = 'candidate' | 'student' | 'listening' | 'reading' | 'writing' | 'average';

export function searchScores(rows: ScoreRow[], query: string): ScoreRow[] {
  const text = fold(query);
  if (!text) return rows;
  const number = digits(query);

  return rows.filter((row) => {
    if (fold(row.student.fullName).includes(text)) return true;
    return number.length > 0 && digits(row.student.candidateNumber).includes(number);
  });
}

/** A band, or nothing — a paper not sat, or one not marked yet. */
const bandOf = (cell: ScoreCell | null): number | null =>
  cell && !cell.awaitingMarking ? cell.band : null;

const value = (row: ScoreRow, key: ScoreSortKey): number | null => {
  switch (key) {
    case 'listening':
      return bandOf(row.listening);
    case 'reading':
      return bandOf(row.reading);
    case 'writing':
      return bandOf(row.writing);
    case 'average':
      return row.average;
    default:
      return null;
  }
};

/**
 * Sorted, with the blanks always at the bottom.
 *
 * A candidate who has not sat Reading has no Reading band, and sorting by
 * Reading is a question about the people who have. Putting them last either
 * way round keeps the top of the table meaning what it looks like it means.
 */
export function sortScores(
  rows: ScoreRow[],
  key: ScoreSortKey,
  direction: SortDirection,
): ScoreRow[] {
  const sign = direction === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    if (key === 'student' || key === 'candidate') {
      const first =
        key === 'student'
          ? a.student.fullName.localeCompare(b.student.fullName)
          : digits(a.student.candidateNumber).localeCompare(
              digits(b.student.candidateNumber),
              undefined,
              { numeric: true },
            );
      return first !== 0 ? first * sign : a.student.fullName.localeCompare(b.student.fullName);
    }

    const left = value(a, key);
    const right = value(b, key);
    if (left === null && right === null) return a.student.fullName.localeCompare(b.student.fullName);
    if (left === null) return 1;
    if (right === null) return -1;
    if (left === right) return a.student.fullName.localeCompare(b.student.fullName);
    return (left - right) * sign;
  });
}

export const arrangeScores = (
  rows: ScoreRow[],
  options: { query: string; key: ScoreSortKey; direction: SortDirection },
): ScoreRow[] => sortScores(searchScores(rows, options.query), options.key, options.direction);
